import { Stack, Box, Group, Text, Badge, LoadingOverlay } from '@mantine/core'
import { IconFileText, IconChevronRight, IconReportMoney } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { WidgetCard, EmptyState } from '@/components/ui'
import { finopsApi } from '@/api/finops'
import { formatUSD } from '@/utils/finops'

function formatGeneratedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR')
  } catch {
    return iso
  }
}

export function RelatoriosV2() {
  const navigate = useNavigate()

  const { data: reports, isLoading } = useQuery({
    queryKey: ['finops', 'reports', 'list'],
    queryFn: () => finopsApi.getReports({ limit: 100 }),
  })

  return (
    <Stack gap="lg">

      <Box style={{ position: 'relative', minHeight: 160 }}>
        <LoadingOverlay visible={isLoading} zIndex={2} />

        {!isLoading && (!reports || reports.length === 0) ? (
          <EmptyState
            icon={IconReportMoney}
            title="Nenhum relatório disponível"
            description={
              <>
                O poller do <code>finops_reports_poller</code> ainda não ingeriu
                nenhum relatório semanal. Isso é esperado se o Lambda gerador
                não rodou ainda, ou se o bucket S3 está vazio.
              </>
            }
            accent="info"
            size="lg"
          />
        ) : (
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 16,
            }}
          >
            {(reports ?? []).map(report => (
              <WidgetCard
                key={report.id}
                accent="primary"
                onClick={() => navigate(`/finops/reports/${report.id}`)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(`/finops/reports/${report.id}`)
                  }
                }}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                p="md"
              >
                <Stack gap="xs">
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Box>
                      <Group gap="xs" mb={4}>
                        <IconFileText size={16} color="var(--ai-brand-verde)" />
                        <Text size="sm" fw={600}>
                          Semana {report.week}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Gerado em {formatGeneratedAt(report.generated_at)}
                      </Text>
                    </Box>
                    <IconChevronRight size={16} color="var(--ai-text-muted)" />
                  </Group>

                  {(report.period_start || report.period_end) && (
                    <Text size="xs" c="var(--ai-text-secondary)">
                      Período: {report.period_start ?? '—'} → {report.period_end ?? '—'}
                    </Text>
                  )}

                  <Group justify="space-between" align="center" mt={4}>
                    <Text size="xs" c="dimmed">
                      Custo total
                    </Text>
                    <Badge color="teal" variant="light" size="lg">
                      {formatUSD(report.total_cost_usd)}
                    </Badge>
                  </Group>
                </Stack>
              </WidgetCard>
            ))}
          </Box>
        )}
      </Box>
    </Stack>
  )
}
